import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api/client";
import type { Playlist, PlaylistItem, PlaylistView, StreamSet } from "../api/types";
import { copyText, pitchWithRate, playError, registerAudio, stopEmbed, unlock } from "../components/player";
import { fmtDur } from "../components/Modal";
import { useRights } from "../rights";
import { when } from "./admin/bits";

/**
 * My playlists: the list of them, and one of them open.
 *
 * Two views on one route family -- /playlists and /playlists/{id} -- kept in
 * one file because they share the item row and the player, and because the
 * hand-rolled router in App.tsx only wants one component per path family.
 * A shared list reached by link (/p/{token}) is the same open view, read-only.
 */
export function Playlists({ id, token, go }: {
  id?: number;
  token?: string;
  go: (path: string) => void;
}) {
  if (token) return <One token={token} go={go} />;
  if (id) return <One id={id} go={go} />;
  return <Mine go={go} />;
}

// ---------------------------------------------------------------------------

function Mine({ go }: { go: (path: string) => void }) {
  const [lists, setLists] = useState<Playlist[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");

  const load = useCallback(() => {
    api.playlists().then(setLists).catch((e: ApiError) => setError(e.message));
  }, []);
  useEffect(load, [load]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    try {
      const { id } = await api.createPlaylist({ name: n });
      setName("");
      go(`/playlists/${id}`);
    } catch (err) {
      setError((err as ApiError).message);
    }
  }

  return (
    <div className="playlists">
      <h2 className="pl-h">Playlists</h2>
      <p className="dim small">
        Tracks you kept, in the order you put them. A record goes on whole from
        its card, a single track from its row in the per-track view. A list you
        share is readable by anyone signed in who has the link.
      </p>
      {error && <div className="error">{error}</div>}

      <form className="addrow" onSubmit={(e) => void create(e)}>
        <input value={name} onChange={(e) => setName(e.target.value)}
               placeholder="New playlist…" maxLength={120} />
        <button type="submit" disabled={!name.trim()}>Make</button>
      </form>

      {lists && lists.length === 0 && (
        <div className="empty">
          No playlists yet. Name one above, or press <b>+</b> on any card.
        </div>
      )}
      {lists && lists.length > 0 && (
        <table className="runs pl-list">
          <thead><tr><th>name</th><th className="num">tracks</th><th>shared</th><th>changed</th></tr></thead>
          <tbody>
            {lists.map((p) => (
              <tr key={p.id} onClick={() => go(`/playlists/${p.id}`)} className="row">
                <td><a href={`/playlists/${p.id}`} onClick={(e) => { e.preventDefault(); go(`/playlists/${p.id}`); }}>{p.name}</a>
                    {p.description && <span className="dim small"> · {p.description}</span>}</td>
                <td className="num">{p.count}</td>
                <td className="dim">{p.shared ? "yes" : "—"}</td>
                <td className="dim">{when(p.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!lists && !error && <div className="loading"><span className="spinner" />loading…</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * One playlist, and its player.
 *
 * The player is this page's own rather than a rewrite of player.ts. That
 * module knows one thing -- which release is playing -- because the Bandcamp
 * iframe can only be stopped by unmounting it; a playlist wants a track and a
 * next. So this owns a queue of its own and registers with registerAudio(),
 * exactly as the Speed test does, so a card starting elsewhere silences it
 * and vice versa. Stream URLs are minted per release as playback reaches
 * each item, never for the whole list at once (D6).
 */
function One({ id, token, go }: { id?: number; token?: string; go: (path: string) => void }) {
  const [pl, setPl] = useState<PlaylistView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(() => {
    const p = token ? api.sharedPlaylist(token) : api.playlist(id!);
    p.then(setPl).catch((e: ApiError) => setError(e.message));
  }, [id, token]);
  useEffect(load, [load]);

  // --- the player ----------------------------------------------------------
  const audio = useRef<HTMLAudioElement | null>(null);
  const urls = useRef<Map<string, StreamSet>>(new Map()); // release -> set
  const [playing, setPlaying] = useState<number | null>(null); // item id
  const [paused, setPaused] = useState(false);
  const [pct, setPct] = useState(0); // the fader, kept while moving between tracks
  const [failed, setFailed] = useState<Record<number, string>>({});

  useEffect(() => {
    const el = new Audio();
    el.preload = "none";
    audio.current = el;
    const unregister = registerAudio(() => { el.pause(); });
    el.onpause = () => setPaused(true);
    el.onplay = () => setPaused(false);
    return () => { el.pause(); el.src = ""; unregister(); };
  }, []);

  const rate = 1 + pct / 100;
  useEffect(() => {
    if (audio.current) pitchWithRate(audio.current, rate);
  }, [rate, playing]);

  async function urlFor(it: PlaylistItem): Promise<string | null> {
    if (!it.release || it.pos == null) return null;
    let set = urls.current.get(it.release);
    if (!set) {
      set = await api.streams(it.release);
      urls.current.set(it.release, set);
    }
    const hit = set.tracks.find((t) => t.pos === it.pos && t.kind !== "embed");
    return hit?.url ?? null;
  }

  const items = pl?.items ?? [];

  async function playAt(i: number) {
    const el = audio.current;
    if (!el) return;
    // Skip what cannot play: gone from the catalogue, or nobody streams it.
    let j = i;
    while (j < items.length && !(items[j]!.listed && items[j]!.streamable)) j++;
    if (j >= items.length) { setPlaying(null); return; }
    const it = items[j]!;
    try {
      const url = await urlFor(it);
      if (!url) {
        setFailed((f) => ({ ...f, [it.id]: "nobody is streaming this track" }));
        return void playAt(j + 1);
      }
      stopEmbed();
      el.src = url;
      pitchWithRate(el, rate);
      el.onloadedmetadata = () => { pitchWithRate(el, rate); };
      el.onended = () => void playAt(j + 1);
      await el.play();
      setPlaying(it.id);
    } catch (e) {
      setFailed((f) => ({ ...f, [it.id]: playError(e) }));
      // A browser refusing to play without a tap would refuse the next one
      // too; that is not a reason to walk the whole list marking failures.
      if ((e as { name?: string }).name === "NotAllowedError") { setPlaying(null); return; }
      void playAt(j + 1);
    }
  }

  function toggle(i: number) {
    const el = audio.current;
    const it = items[i];
    if (!el || !it) return;
    if (playing === it.id) {
      if (el.paused) void el.play(); else el.pause();
      return;
    }
    void playAt(i);
  }

  // --- the owner's edits -----------------------------------------------------
  const mine = !!pl?.mine && !token;
  const rights = useRights();

  async function edit(fn: () => Promise<unknown>, done?: string) {
    setError(null);
    try { await fn(); load(); if (done) setNote(done); } catch (e) { setError((e as ApiError).message); }
  }

  function move(i: number, d: -1 | 1) {
    const j = i + d;
    if (!pl || j < 0 || j >= items.length) return;
    const order = items.map((x) => x.id);
    [order[i], order[j]] = [order[j]!, order[i]!];
    // Draw the new order at once; the server confirms or the reload corrects.
    const next = [...items];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setPl({ ...pl, items: next });
    void edit(() => api.reorderPlaylist(pl.id, order));
  }

  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState({ name: "", description: "" });
  useEffect(() => {
    if (pl) setDraft({ name: pl.name, description: pl.description ?? "" });
  }, [pl?.id, pl?.name, pl?.description]); // eslint-disable-line react-hooks/exhaustive-deps

  const shareURL = pl?.shareToken ? `${window.location.origin}/p/${pl.shareToken}` : "";
  const total = items.reduce((n, x) => n + (x.dur ?? 0), 0);
  const playable = items.filter((x) => x.listed && x.streamable).length;
  const now = items.find((x) => x.id === playing);
  const shifted = now?.bpm ? (now.bpm * rate).toFixed(1) : null;

  if (error && !pl) {
    return (
      <div className="playlists">
        <div className="error">{error}</div>
        <button className="link" onClick={() => go("/playlists")}>← all playlists</button>
      </div>
    );
  }
  if (!pl) return <div className="loading"><span className="spinner" />loading…</div>;

  return (
    <div className="playlists one">
      <div className="pl-top">
        {!token && <button className="link back" onClick={() => go("/playlists")}>← all playlists</button>}
        {token && <span className="dim small">shared by <b>{pl.ownerName || "someone"}</b></span>}
      </div>

      {renaming ? (
        <form className="pl-rename" onSubmit={(e) => {
          e.preventDefault();
          void edit(() => api.updatePlaylist(pl.id, {
            name: draft.name.trim(), description: draft.description.trim(),
          }), "saved");
          setRenaming(false);
        }}>
          <input value={draft.name} maxLength={120} autoFocus
                 onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          <input value={draft.description} maxLength={2000} placeholder="a line about it (optional)"
                 onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          <button type="submit" disabled={!draft.name.trim()}>Save</button>
          <button type="button" className="link" onClick={() => setRenaming(false)}>cancel</button>
        </form>
      ) : (
        <h2 className="pl-h">
          {pl.name}
          {mine && <button className="link" onClick={() => setRenaming(true)}>rename</button>}
        </h2>
      )}
      {pl.description && !renaming && <p className="dim">{pl.description}</p>}

      <div className="pl-meta dim small">
        {items.length} track{items.length === 1 ? "" : "s"}
        {total > 0 && <> · {fmtDur(total)}</>}
        {playable < items.length && <> · {playable} playable</>}
        {" · "}changed {when(pl.updatedAt)}
      </div>

      {error && <div className="error">{error}</div>}
      {note && <div className="pl-note" onAnimationEnd={() => setNote(null)}>{note}</div>}

      {/* the transport: play all, the fader, and what is playing */}
      <div className="pl-bar">
        <button className="primary" disabled={playable === 0}
                onClick={() => {
                  const el = audio.current;
                  if (!el) return;
                  if (playing !== null) { el.paused ? void el.play().catch(() => undefined) : el.pause(); return; }
                  unlock(el); // inside the tap, for iOS -- see player.ts
                  void playAt(0);
                }}>
          {playing !== null ? (paused ? "▶ Resume" : "❚❚ Pause") : "▶ Play all"}
        </button>
        <div className="fader">
          <button className="zero" onClick={() => setPct(0)}>0%</button>
          <input type="range" min={-16} max={16} step={0.1} value={pct}
                 onChange={(e) => setPct(Number(e.target.value))}
                 title="±16%: the range of a 1210. Shifts pitch with tempo, as the fader does." />
          <span className="read">
            <b>{pct > 0 ? "+" : ""}{pct.toFixed(1)}%</b>
            {shifted && <> · <span className="bpm">{shifted} BPM</span></>}
          </span>
        </div>
        {now && <span className="now dim small">{now.artist} – {now.title}</span>}
      </div>

      {items.length === 0 && (
        <div className="empty">
          Nothing on it yet. {mine && <>Press <b>+</b> on a card, or <b>⋯</b> on a track row.</>}
        </div>
      )}

      {items.length > 0 && (
        <table className="tracklist pl-items">
          <tbody>
            {items.map((it, i) => {
              const on = playing === it.id;
              const can = it.listed && it.streamable;
              return (
                <tr key={it.id} className={`${on ? "playing" : ""}${it.listed ? "" : " gone"}`}>
                  <td className="play">
                    <button className={`p${on && !paused ? " on" : ""}`} disabled={!can}
                            onClick={() => toggle(i)}
                            title={!it.listed ? "no longer listed" : !it.streamable ? "nobody serves audio for this track" : on && !paused ? "pause" : "play"}>
                      {on && !paused ? "❚❚" : "▶"}
                    </button>
                  </td>
                  <td className="ord dim">{i + 1}</td>
                  <td className="art">{it.art && <img src={it.art} alt="" loading="lazy" width={40} height={40} />}</td>
                  <td className="t">
                    <span className="tt">{it.title}</span>
                    <span className="ta">
                      {it.artist}
                      {it.via && <span className="via"> · clip from {it.via}</span>}
                      {!it.listed && <span className="via"> · no longer listed</span>}
                    </span>
                    {failed[it.id] && <span className="err">{failed[it.id]}</span>}
                  </td>
                  <td className="al">
                    {it.release && it.listed
                      ? <a href={`/?q=${encodeURIComponent(it.album)}`} title="find the record in the catalogue">{it.album}</a>
                      : <span className="dim">{it.album}</span>}
                  </td>
                  <td className="d">{it.dur ? fmtDur(it.dur) : ""}</td>
                  <td className={`b${it.bpm ? "" : " none"}`}>{it.bpm ?? "—"}</td>
                  <td className={`k${it.key_ ? "" : " none"}`}>{it.key_ ?? "—"}</td>
                  {mine && (
                    <td className="ops">
                      <button className="link" disabled={i === 0} onClick={() => move(i, -1)} title="move up">↑</button>
                      <button className="link" disabled={i === items.length - 1} onClick={() => move(i, 1)} title="move down">↓</button>
                      <button className="link" onClick={() => void edit(() => api.removeFromPlaylist(pl.id, it.id))} title="take it off the list">×</button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {mine && (
        <section className="pl-share">
          {rights.has("sharing") ? (
          <label className={`chk${pl.shared ? " on" : ""}`}>
            <input type="checkbox" checked={pl.shared}
                   onChange={(e) => void edit(() => api.updatePlaylist(pl.id, { shared: e.target.checked }))} />
            Shared — anyone signed in with the link can read it
          </label>
          ) : (
            <span className="dim small">
              {pl.shared ? "Shared by link. " : ""}Sharing is not switched on for your account; an admin can give you the feature.
            </span>
          )}
          {rights.has("sharing") && pl.shared && shareURL && (
            <div className="pl-link">
              <code>{shareURL}</code>
              <button className="link" onClick={() => void copyText(shareURL).then((ok) => setNote(ok ? "link copied" : "could not copy -- select the link and copy it"))}>copy</button>
              <button className="link" title="the old link stops working"
                      onClick={() => void edit(() => api.newShareToken(pl.id), "new link made")}>new link</button>
            </div>
          )}
          <div className="pl-danger">
            <button className="link danger" onClick={() => {
              if (confirmDelete.current) {
                void edit(() => api.deletePlaylist(pl.id)).then(() => go("/playlists"));
              } else {
                confirmDelete.current = true;
                setNote("press delete again to confirm");
                setTimeout(() => { confirmDelete.current = false; }, 4000);
              }
            }}>delete this playlist</button>
          </div>
        </section>
      )}
    </div>
  );
}

// A two-press delete, without a browser confirm() -- those block the page.
const confirmDelete = { current: false };
